import { EditRoleSection } from './EditRoleSection';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/routes';
import { DarwinDivider, DarwinFlex, DarwinSpin } from 'components/darwin-ui';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import styles from '../Roles.module.css';
import columnStyles from './index.module.css';
import PermissionSection from './PermissionSection';
import { WizardSection } from './WizardSection';
import { TreeSection } from './TreeSection';
import { RoleDetails } from './RoleDetails';
import { useRoleDetails } from 'hooks/useRoleDetails';
import { extractKeysFromProducts } from './TreeSection/utils';
import { useCreateRole, useUpdateRole } from 'api/services/rolesService';
import { useUI } from 'contexts/UiContext';

export enum RoleSections {
  RoleDetails,
  AssociatedProducts,
  AssociatedProcesses,
  AssociatedUsers,
  Permissions,
}

export const EditRoles = () => {
  const [activeSection, setActiveSection] = useState<RoleSections>(
    RoleSections.RoleDetails
  );
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const navigate = useNavigate();
  const [productKeys, setProductKeys] = useState<React.Key[]>([]);
  const [processesKeys, setProcessesKeys] = useState<React.Key[]>([]);
  const [usersKeys, setUsersKeys] = useState<React.Key[]>([]);
  const [roleFormValues, setRoleFormValues] = useState({
    name: '',
    description: '',
  });
  const { selectedCompany } = useUI();
  const { mutateAsync: createRole, isPending: createRoleisPending } =
    useCreateRole();
  const { mutateAsync: updateRole, isPending: updateRoleisPending } =
    useUpdateRole();

  const { pathname } = useLocation();
  const isEdit = pathname.includes('/edit-role');
  const [isFormValid, setIsFormValid] = useState(() => isEdit);

  const handleAssociatedUsers = (keys: React.Key[]) => {
    setUsersKeys(keys);
  };

  const handleAssociatedProducts = (keys: React.Key[]) => {
    setProductKeys(keys);
  };

  const handleAssociatedProcesses = (keys: React.Key[]) => {
    setProcessesKeys(keys);
  };
  const {
    roleDetails,
    associatedProducts,
    associatedProcesses,
    userCompanyTreeData,
    isLoading,
    isError,
  } = useRoleDetails();

  const productsSelectedKeys = useMemo(
    () => extractKeysFromProducts(roleDetails?.products || []),
    [roleDetails]
  );

  const processesSelectedKeys = useMemo(
    () => roleDetails?.processes?.map(process => `procces-${process.id}`) || [],
    [roleDetails]
  );

  const usersSelectedKeys = useMemo(
    () => roleDetails?.users?.map(user => `user-${user.id}`) || [],
    [roleDetails]
  );

  useEffect(() => {
    setUsersKeys(usersSelectedKeys || []);
  }, [usersSelectedKeys]);

  useEffect(() => {
    setProcessesKeys(processesSelectedKeys || []);
  }, [processesSelectedKeys]);

  useEffect(() => {
    setProductKeys(productsSelectedKeys || []);
  }, [productsSelectedKeys]);

  useEffect(() => {
    if (isError) {
      openNotification(t('error'), t('food-category-error'), 'error');
    }
  }, [t, openNotification, isError]);
  const readonly =
    roleDetails?.type !== 'CREATED_BY_USER' || pathname.includes('/view');

  const handleRoleDetailsChange = (
    values: { name: string; description: string },
    isValid: boolean
  ) => {
    setRoleFormValues(values);
    setIsFormValid(isValid);
  };

  const handleSave = useCallback(async () => {
    if (!roleDetails) return;

    const newRole = {
      name: roleFormValues.name || roleDetails.name,
      description: roleFormValues.description || roleDetails.description,
      type: roleDetails.type,
      permissions: roleDetails.permissions
        .filter(p => p.granted)
        .map(permission => ({
          module: permission.permission.module,
          actions: [permission.permission.action],
        })),
      permissionsHierarchy: {
        commodities: productKeys
          .filter(
            key => typeof key === 'string' && key.startsWith('commodity-')
          )
          .map(key => typeof key === 'string' && key.replace('commodity-', '')),
        varieties: productKeys
          .filter(key => typeof key === 'string' && key.startsWith('variety-'))
          .map(key => typeof key === 'string' && key.replace('variety-', '')),
        products: productKeys
          .filter(key => typeof key === 'string' && key.startsWith('product-'))
          .map(key => typeof key === 'string' && key.replace('product-', '')),
        finalProducts: productKeys
          .filter(
            key => typeof key === 'string' && key.startsWith('finalProduct-')
          )
          .map(
            key => typeof key === 'string' && key.replace('finalProduct-', '')
          ),
      },
      processIds: processesKeys
        .filter(p => p.toString().startsWith('procces-'))
        .map(key => typeof key === 'string' && key.replace('procces-', '')),
    };

    try {
      if (roleDetails.id) {
        // Update role

        await updateRole({
          companyId: selectedCompany || '',
          role: { ...newRole, id: roleDetails.id },
        });
        openNotification(
          t('success'),
          t('roles.role-form.role-updated-successfully'),
          'success'
        );
      } else {
        await createRole({
          companyId: selectedCompany || '',
          role: newRole,
        });
        openNotification(
          t('success'),
          t('roles.role-form.role-created-successfully'),
          'success'
        );
      }
      navigate(ROUTES.ROLES.LIST);
    } catch (error) {
      openNotification(
        t('error'),
        t('roles.role-form.role-creation-failed'),
        'error'
      );
    }
  }, [
    roleDetails,
    productKeys,
    processesKeys,
    t,
    updateRole,
    openNotification,
    navigate,
    roleFormValues.description,
    roleFormValues.name,
    createRole,
    selectedCompany,
  ]);

  return (
    <>
      <DarwinSpin
        fullscreen
        spinning={isLoading || createRoleisPending || updateRoleisPending}
      />
      <ContentHeader
        title={`${roleDetails?.name} ${t('roles.edit-page-title')}`}
        onSave={handleSave}
        onSaveTitle={t('save')}
        onCancel={() => navigate(ROUTES.ROLES.LIST)}
        // if is readonly this buttons must be disabled
        saveDisabled={!isFormValid || createRoleisPending || readonly}
      />
      <DarwinFlex flex={1} className={styles.editRoles}>
        <EditRoleSection
          section={activeSection}
          setSection={setActiveSection}
        />
        <DarwinDivider
          type="vertical"
          style={{ borderColor: '#D8DAE5', height: 'auto' }}
        />
        <DarwinFlex
          align="start"
          justify="center"
          className={columnStyles.editRoleColumn}
        >
          {/* Associated Users section */}
          <>
            {activeSection === RoleSections.RoleDetails && (
              <WizardSection
                title={t('roles.wizard-sections.role-details.title')}
                description={t(
                  'roles.wizard-sections.role-details.description'
                )}
              >
                <RoleDetails
                  name={roleDetails?.name}
                  description={roleDetails?.description}
                  readonly={readonly}
                  onChange={handleRoleDetailsChange}
                />
              </WizardSection>
            )}
            {activeSection === RoleSections.AssociatedUsers && (
              <WizardSection
                title={t('roles.wizard-sections.associated-users.title')}
                description={t(
                  'roles.wizard-sections.associated-users.description'
                )}
              >
                <TreeSection
                  treeData={userCompanyTreeData}
                  initialSelectedKeys={usersKeys}
                  onChange={handleAssociatedUsers}
                  searchPlaceholder={t(
                    'roles.wizard-sections.associated-users.search-placeholder'
                  )}
                  readonly
                />
              </WizardSection>
            )}
            {activeSection === RoleSections.AssociatedProducts && (
              <WizardSection
                title={t('roles.wizard-sections.associated-products.title')}
                description={t(
                  'roles.wizard-sections.associated-products.description'
                )}
              >
                <TreeSection
                  treeData={associatedProducts}
                  searchPlaceholder={t(
                    'roles.wizard-sections.associated-products.search-placeholder'
                  )}
                  onChange={handleAssociatedProducts}
                  initialSelectedKeys={productKeys}
                  readonly={readonly}
                />
              </WizardSection>
            )}
            {activeSection === RoleSections.AssociatedProcesses && (
              <WizardSection
                title={t('roles.wizard-sections.associated-processes.title')}
                description={t(
                  'roles.wizard-sections.associated-processes.description'
                )}
              >
                <TreeSection
                  treeData={associatedProcesses}
                  searchPlaceholder={t(
                    'roles.wizard-sections.associated-processes.search-placeholder'
                  )}
                  onChange={handleAssociatedProcesses}
                  initialSelectedKeys={processesKeys}
                  readonly={readonly}
                />
              </WizardSection>
            )}
          </>

          {/* Permissions Table section */}
          {activeSection === RoleSections.Permissions && (
            <WizardSection
              title={t('roles.wizard-sections.permission-section.title')}
              description={t(
                'roles.wizard-sections.permission-section.description'
              )}
            >
              <PermissionSection permissions={roleDetails?.permissions || []} />
            </WizardSection>
          )}
        </DarwinFlex>
      </DarwinFlex>
    </>
  );
};
